import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  adminCreateProduct,
  adminDeleteProduct,
  adminListProducts,
  adminUpdateProduct,
  listProducts,
} from "./products-api";
import type { ProductInput } from "./types";

const KEY = ["products"] as const;
const ADMIN_KEY = ["admin", "products"] as const;

/** Newest first, a page at a time (the API pages by cursor). */
export const useProducts = () =>
  useInfiniteQuery({
    queryKey: KEY,
    queryFn: ({ pageParam }) => listProducts(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    staleTime: 30_000,
  });

export const useAdminProducts = () =>
  useInfiniteQuery({
    queryKey: ADMIN_KEY,
    queryFn: ({ pageParam }) => adminListProducts(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    staleTime: 30_000,
  });

export function useAdminProductMutations() {
  const qc = useQueryClient();
  // Not awaited: the form closes as soon as the save succeeds; the lists refresh behind it.
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ADMIN_KEY });
    void qc.invalidateQueries({ queryKey: KEY });
  };
  return {
    create: useMutation({ mutationFn: (input: ProductInput) => adminCreateProduct(input), onSettled: invalidate }),
    update: useMutation({
      mutationFn: ({ id, changes }: { id: string; changes: Partial<ProductInput> }) =>
        adminUpdateProduct(id, changes),
      onSettled: invalidate,
    }),
    remove: useMutation({ mutationFn: (id: string) => adminDeleteProduct(id), onSettled: invalidate }),
  };
}
